/**
 * SubmissionQueue — Coach-facing queue of pending submissions.
 *
 * Shows:
 *   - List of submissions with learner name, scene title, submitted time
 *   - Click to open grading view
 *   - AI pre-grade scores shown (coach can adjust)
 *   - Override controls per criterion
 *   - Overall feedback text area
 *   - Submit grade button
 */
import React, { useEffect, useState, useCallback } from 'react';
import { curriculumAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CoachLayout from '../../pages/coach/CoachLayout';

export default function SubmissionQueue({ domain: propDomain, sceneId }) {
  const { authoringDomains, isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [domainFilter, setDomainFilter] = useState(propDomain || '');
  const [domainCatalog, setDomainCatalog] = useState([]);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data }) => setDomainCatalog(Array.isArray(data?.domains) ? data.domains : []))
      .catch(() => setDomainCatalog([]));
  }, []);

  const domains = React.useMemo(() => {
    if (domainCatalog.length) {
      const catalog = isAdmin
        ? domainCatalog
        : domainCatalog.filter((d) => (authoringDomains || []).includes(d.domain_id));
      return catalog.map((d) => ({ id: d.domain_id, label: d.display_name || d.domain_id }));
    }
    return (authoringDomains || []).map((id) => ({ id, label: id }));
  }, [domainCatalog, authoringDomains, isAdmin]);

  const domain = domainFilter || propDomain;

  const fetchSubmissions = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('scenario_os_token');
    const params = new URLSearchParams();
    if (filter) params.set('status', filter);
    if (domain) params.set('domain', domain);
    if (sceneId) params.set('scene_id', sceneId);

    fetch(`/scenario-os/coach/rubrics/submissions?${params}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d))))
      .then((data) => setSubmissions(data.submissions || []))
      .catch((e) => setError(e.detail || e.message || 'Failed to load submissions'))
      .finally(() => setLoading(false));
  }, [filter, domain, sceneId, domainFilter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  if (selected) {
    return (
      <GradingView
        submission={selected}
        onBack={() => { setSelected(null); fetchSubmissions(); }}
      />
    );
  }

  return (
    <CoachLayout title="Submissions">
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: '#1f2937' }}>Submission Queue</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            style={domainSelectStyle}
          >
            <option value="">All domains</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6 }}>
            {['pending', 'ai_graded', 'reviewed', ''].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  ...filterBtnStyle,
                  background: filter === f ? '#3b82f6' : '#f1f5f9',
                  color: filter === f ? '#fff' : '#475569',
                }}
              >
                {f || 'All'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div style={mutedStyle}>Loading submissions…</div>}
      {error && <div style={errorStyle}>{error}</div>}

      {!loading && submissions.length === 0 && (
        <div style={emptyStyle}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>📭</div>
          No {filter || ''} submissions found.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {submissions.map((s) => (
          <button
            key={s.submission_id}
            onClick={() => setSelected(s)}
            style={submissionCardStyle}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937', marginBottom: 2 }}>
                  {s.learner_id}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Scene: {s.scene_id} · {s.domain}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={statusBadge(s.status)}>{s.status}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : ''}
                </div>
              </div>
            </div>
            {s.text && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.4 }}>
                {s.text.slice(0, 120)}{s.text.length > 120 ? '…' : ''}
              </div>
            )}
            {s.ai_overall_pct != null && (
              <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 4, fontWeight: 600 }}>
                AI Score: {s.ai_overall_pct}%
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
    </CoachLayout>
  );
}


function GradingView({ submission, onBack }) {
  const [rubric, setRubric] = useState(null);
  const [scores, setScores] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiGrading, setAiGrading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('scenario_os_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    fetch(`/scenario-os/coach/rubrics/${encodeURIComponent(submission.scene_id)}`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setRubric(data);
          const existingScores = submission.scores || submission.ai_scores || [];
          const initial = (data.criteria || []).map((c) => {
            const existing = existingScores.find((s) => s.criterion_name === c.name);
            return {
              criterion_name: c.name,
              score: existing?.score ?? 0,
              feedback: existing?.feedback || existing?.reasoning || '',
            };
          });
          setScores(initial);
        }
      })
      .catch(() => {});
    setFeedback(submission.overall_feedback || '');
  }, [submission]);

  const triggerAiGrade = async () => {
    setAiGrading(true);
    setError('');
    try {
      const res = await fetch(
        `/scenario-os/coach/rubrics/submissions/${encodeURIComponent(submission.submission_id)}/ai-grade`,
        { method: 'POST', headers }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.ai_scores) {
        setScores((prev) =>
          prev.map((s) => {
            const ai = data.ai_scores.find((a) => a.criterion_name === s.criterion_name);
            if (ai) return { ...s, score: ai.score, feedback: ai.reasoning || ai.feedback || s.feedback };
            return s;
          })
        );
      }
    } catch (e) {
      setError(e.message || 'AI grading failed.');
    } finally {
      setAiGrading(false);
    }
  };

  const updateScore = (idx, field, value) => {
    setScores((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const submitGrade = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(
        `/scenario-os/coach/rubrics/submissions/${encodeURIComponent(submission.submission_id)}/grade`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ scores, overall_feedback: feedback }),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      setSuccess('Grade submitted successfully.');
      setTimeout(onBack, 1500);
    } catch (e) {
      setError(e.message || 'Failed to submit grade.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={containerStyle}>
      <button onClick={onBack} style={backBtnStyle}>← Back to Queue</button>

      <div style={{ marginTop: 12, marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#1f2937' }}>
          Grade Submission
        </h2>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          Learner: <strong>{submission.learner_id}</strong> · Scene: {submission.scene_id}
        </div>
      </div>

      {/* Submission text */}
      <div style={submissionTextStyle}>
        <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', marginBottom: 6 }}>
          Learner Submission
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: '#1f2937' }}>
          {submission.text || '(No text submitted)'}
        </div>
        {submission.file_url && (
          <a href={submission.file_url} target="_blank" rel="noopener noreferrer"
             style={{ display: 'inline-block', marginTop: 8, color: '#3b82f6', fontSize: 13 }}>
            View attached file
          </a>
        )}
      </div>

      {/* AI grade button */}
      <button
        onClick={triggerAiGrade}
        disabled={aiGrading}
        style={aiGradeBtnStyle}
      >
        {aiGrading ? 'AI is grading…' : 'Run AI Pre-Grade'}
      </button>

      {/* Per-criterion grading */}
      {rubric && scores.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={sectionTitleStyle}>Criteria Scores</h3>
          {scores.map((s, i) => {
            const criterion = (rubric.criteria || []).find((c) => c.name === s.criterion_name) || {};
            const levels = criterion.levels || [];

            return (
              <div key={i} style={criterionGradeStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>
                      {s.criterion_name}
                    </span>
                    {criterion.description && (
                      <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
                        {criterion.description}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    Weight: {criterion.weight || 1}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                  {levels.map((l, j) => (
                    <button
                      key={j}
                      onClick={() => updateScore(i, 'score', l.score)}
                      style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.1s',
                        border: s.score === l.score ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        background: s.score === l.score ? '#eff6ff' : '#fff',
                        color: s.score === l.score ? '#1d4ed8' : '#374151',
                      }}
                    >
                      {l.label} ({l.score})
                    </button>
                  ))}
                </div>

                <textarea
                  value={s.feedback}
                  onChange={(e) => updateScore(i, 'feedback', e.target.value)}
                  placeholder="Feedback for this criterion…"
                  rows={2}
                  style={feedbackInputStyle}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Overall feedback */}
      <div style={{ marginTop: 16 }}>
        <h3 style={sectionTitleStyle}>Overall Feedback</h3>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Write overall feedback for the learner…"
          rows={4}
          style={{ ...feedbackInputStyle, fontSize: 14 }}
        />
      </div>

      {/* Submit */}
      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={submitGrade} disabled={saving} style={submitBtnStyle}>
          {saving ? 'Submitting…' : 'Submit Grade'}
        </button>
        <button onClick={onBack} style={cancelBtnStyle}>Cancel</button>
      </div>

      {error && <div style={errorBannerStyle}>{error}</div>}
      {success && <div style={successBannerStyle}>{success}</div>}
    </div>
  );
}


// ── Styles ───────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  maxWidth: 780,
  margin: '0 auto',
  padding: 20,
};
const mutedStyle = { color: '#6b7280', fontSize: 14, padding: '24px 0' };
const errorStyle = {
  color: '#b3261e', background: 'rgba(239,68,68,0.08)',
  border: '1px solid rgba(239,68,68,0.3)', padding: 12, borderRadius: 6, fontSize: 14,
};
const emptyStyle = {
  textAlign: 'center', color: '#6b7280', fontSize: 14, padding: '32px 0',
};
const domainSelectStyle = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db',
  fontSize: 13, color: '#1e293b', background: '#fff',
};
const filterBtnStyle = {
  border: 'none', borderRadius: 6, padding: '5px 12px',
  fontSize: 12, fontWeight: 600, cursor: 'pointer',
  textTransform: 'capitalize',
};
const submissionCardStyle = {
  width: '100%', textAlign: 'left', background: '#fff',
  border: '1px solid #e5e7eb', borderRadius: 10, padding: 14,
  cursor: 'pointer', transition: 'border-color 0.15s',
  fontFamily: 'inherit', fontSize: 'inherit',
};
const backBtnStyle = {
  background: 'none', border: 'none', color: '#3b82f6',
  cursor: 'pointer', fontSize: 14, padding: 0, fontWeight: 500,
};
const submissionTextStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
  padding: 16, marginBottom: 16,
};
const aiGradeBtnStyle = {
  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
  color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontSize: 13, fontWeight: 700,
  cursor: 'pointer', width: '100%',
};
const sectionTitleStyle = {
  fontSize: 13, fontWeight: 700, color: '#475569',
  textTransform: 'uppercase', letterSpacing: 0.5,
  marginBottom: 10, marginTop: 0,
};
const criterionGradeStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
  padding: 14, marginBottom: 10,
};
const feedbackInputStyle = {
  width: '100%', padding: '7px 10px', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
  lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
};
const submitBtnStyle = {
  background: '#059669', color: '#fff', border: 'none', borderRadius: 6,
  padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const cancelBtnStyle = {
  background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: 6, padding: '9px 18px', fontSize: 13, cursor: 'pointer',
};
const errorBannerStyle = {
  marginTop: 12, padding: '8px 12px', background: '#fef2f2',
  border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13,
};
const successBannerStyle = {
  marginTop: 12, padding: '8px 12px', background: '#f0fdf4',
  border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13,
};

function statusBadge(status) {
  const colors = {
    pending: { bg: '#fef3c7', fg: '#92400e' },
    ai_graded: { bg: '#dbeafe', fg: '#1e40af' },
    reviewed: { bg: '#dcfce7', fg: '#166534' },
  };
  const c = colors[status] || colors.pending;
  return {
    display: 'inline-block',
    background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 999, textTransform: 'capitalize',
  };
}
